import { defineStore } from 'pinia'
import api from '@/utils/api'

export const useBusinessHoursStore = defineStore('businessHours', {
  state: () => ({
    businessHours: [],
    loading: false,
    error: null,
    lastFetched: null
  }),
  getters: {
    activeBusinessHours: (state) => {
      return state.businessHours.filter(hour => !hour.is_closed)
    },
    closedDays: (state) => {
      return state.businessHours.filter(hour => hour.is_closed)
    },
    groupedBusinessHours: (state) => {
      if (!state.businessHours || state.businessHours.length === 0) return []
      
      const hourGroups = {}
      state.businessHours.forEach(item => {
        if (item.is_closed) {
          const hoursStr = 'Closed'
          if (!hourGroups[hoursStr]) hourGroups[hoursStr] = []
          hourGroups[hoursStr].push(item.day)
        } else {
          const openTime = item.open_time ? formatTime(item.open_time) : ''
          const closeTime = item.close_time ? formatTime(item.close_time) : ''
          const hoursStr = `${openTime} - ${closeTime}`
          if (!hourGroups[hoursStr]) hourGroups[hoursStr] = []
          hourGroups[hoursStr].push(item.day)
        }
      })
      
      return Object.entries(hourGroups).map(([hours, days]) => ({ days, hours }))
    },
    needsRefresh: (state) => {
      // Refresh data if it's older than 30 minutes or if no data exists
      if (!state.lastFetched) return true
      const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000)
      return state.lastFetched < thirtyMinutesAgo
    }
  },
  actions: {
    async fetchBusinessHours(force = false) {
      // Don't fetch if already loading
      if (this.loading && !force) return
      
      // Don't fetch if data is fresh and not forcing
      if (!this.needsRefresh && !force && this.businessHours.length > 0) return
      
      this.loading = true
      this.error = null
      try {
        const data = await api.get('/business-hours')
        this.businessHours = data
        this.lastFetched = Date.now()
      } catch (error) {
        this.error = error.message || 'Failed to fetch business hours'
        // Only clear data if we don't have any existing data
        if (this.businessHours.length === 0) {
          this.businessHours = []
        }
      } finally {
        this.loading = false
      }
    }
  }
})

// Helper function to format time from 24-hour to 12-hour format
function formatTime(timeString) {
  if (!timeString) return ''
  
  const [hours, minutes] = timeString.split(':')
  const hour = parseInt(hours)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  
  return `${displayHour}:${minutes} ${ampm}`
} 